import { useCallback, useEffect, useState } from "react";
import { useSession } from "@/auth/SessionProvider";
import { resolveKnowledgeOrgId } from "@/lib/knowledgeOrgId";
import {
  KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED,
  listCustomLibraries,
  removeCustomLibrary,
  type KnowledgeCustomLibrary,
} from "@/lib/knowledgeLibraryRegistry";
import {
  apiCreateKnowledgeLibrary,
  apiDeleteKnowledgeLibrary,
  apiUpdateKnowledgeLibrary,
} from "@/lib/knowledgeRegistryApi";
import { refreshKnowledgeRegistryFromApi } from "@/lib/knowledgeRegistryCrossSync";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

export function useCustomLibraries() {
  const session = useSession();
  const orgId = resolveKnowledgeOrgId(session.data?.user?.orgId);
  const { activeId } = useKnowledgeBases();
  const [libraries, setLibraries] = useState<KnowledgeCustomLibrary[]>(() =>
    listCustomLibraries(orgId, activeId),
  );

  const reloadLocal = useCallback(() => {
    setLibraries(listCustomLibraries(orgId, activeId));
  }, [orgId, activeId]);

  const reload = useCallback(async () => {
    if (orgId) await refreshKnowledgeRegistryFromApi(orgId);
    reloadLocal();
  }, [orgId, reloadLocal]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    function onChange() {
      reloadLocal();
    }
    window.addEventListener(KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(KNOWLEDGE_CUSTOM_LIBRARIES_CHANGED, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [reloadLocal]);

  const createLibrary = useCallback(
    async (input: { title: string; description?: string; id?: string }) => {
      if (!(activeId || "").trim()) {
        return { ok: false as const, error: "Select or create a knowledge base first." };
      }
      const res = await apiCreateKnowledgeLibrary(activeId, input);
      if (!res.ok || !res.data?.library) {
        return { ok: false as const, error: res.error || "Could not create library." };
      }
      if (orgId) await refreshKnowledgeRegistryFromApi(orgId);
      reloadLocal();
      return { ok: true as const, library: res.data.library };
    },
    [orgId, activeId, reloadLocal],
  );

  const updateLibrary = useCallback(
    async (libraryId: string, input: { title: string; description?: string }) => {
      if (!(activeId || "").trim()) {
        return { ok: false as const, error: "Select or create a knowledge base first." };
      }
      const res = await apiUpdateKnowledgeLibrary(activeId, libraryId, input);
      if (!res.ok || !res.data?.library) {
        return { ok: false as const, error: res.error || "Could not update library." };
      }
      if (orgId) await refreshKnowledgeRegistryFromApi(orgId);
      reloadLocal();
      return { ok: true as const, library: res.data.library };
    },
    [orgId, activeId, reloadLocal],
  );

  const deleteLibrary = useCallback(
    async (libraryId: string) => {
      if (!(activeId || "").trim()) {
        return { ok: false as const, error: "Select or create a knowledge base first." };
      }
      const res = await apiDeleteKnowledgeLibrary(activeId, libraryId);
      if (!res.ok) {
        return { ok: false as const, error: res.error || "Could not delete library." };
      }
      // Drop local cache first so refresh cannot re-upload the deleted library.
      removeCustomLibrary(orgId, libraryId, activeId);
      if (orgId) await refreshKnowledgeRegistryFromApi(orgId, { skipLocalPush: true });
      reloadLocal();
      return { ok: true as const };
    },
    [orgId, activeId, reloadLocal],
  );

  return {
    libraries,
    orgId,
    kbId: activeId,
    createLibrary,
    updateLibrary,
    deleteLibrary,
    reload,
  };
}
